import React, { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { DetailSkeleton } from "@/components/stays/Skeletons";
import { getProperty, getRooms, getPropertyGeo, getReviews, friendlyError, fmtINR } from "@/lib/stayflowApi";
import { setMeta, SITE_URL } from "@/lib/seo";
import PropertyDetailMobile from "./PropertyDetailMobile";
import PropertyDetailDesktop from "./PropertyDetailDesktop";

const PropertyDetail = () => {
  const { propertyId } = useParams();
  const [sp] = useSearchParams();
  const qs = sp.toString() ? `?${sp.toString()}` : "";

  // Mount only one layout tree (avoids duplicate testids + double map/image loads).
  // Mobile app-style page for <1024px; full luxury desktop layout for ≥1024px.
  const [isDesktop, setIsDesktop] = React.useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const m = window.matchMedia("(min-width: 1024px)");
    const on = () => setIsDesktop(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);

  const propQ = useQuery({ queryKey: ["property", propertyId], queryFn: () => getProperty(propertyId), staleTime: 5 * 60 * 1000, retry: 1 });
  const roomsQ = useQuery({ queryKey: ["rooms", propertyId], queryFn: () => getRooms(propertyId), staleTime: 5 * 60 * 1000, retry: 1 });
  const geoQ = useQuery({ queryKey: ["property-geo", propertyId], queryFn: () => getPropertyGeo(propertyId), staleTime: 10 * 60 * 1000, retry: 1 });
  const reviewsQ = useQuery({ queryKey: ["reviews", propertyId], queryFn: () => getReviews(propertyId), staleTime: 30 * 60 * 1000, retry: 1 });

  const p = propQ.data;
  const geo = geoQ.data;
  const rev = reviewsQ.data;
  const rooms = Array.isArray(roomsQ.data) ? roomsQ.data : [];

  useEffect(() => {
    if (p) {
      setMeta({
        title: `${p.name} — StayFlow`,
        description: p.description || `Stay at ${p.name}${p.city ? " in " + p.city : ""}. Book with instant confirmation.`,
        image: p.cover_image || undefined,
        url: `${SITE_URL}/stays/${propertyId}`,
      });
    }
  }, [p, propertyId]);

  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/stayflow/share/${propertyId}`;
  const onShare = async () => {
    const priceTxt = p?.min_price_per_night ? ` · from ${fmtINR(p.min_price_per_night)}/night` : "";
    const shareData = { title: p?.name, text: `${p?.name}${priceTxt} on StayFlow`, url: shareUrl };
    try {
      if (navigator.share) { await navigator.share(shareData); return; }
    } catch (err) {
      if (err?.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied — paste it anywhere!");
    } catch {
      window.open(`https://wa.me/?text=${encodeURIComponent(`${shareData.text} ${shareUrl}`)}`, "_blank", "noopener");
    }
  };

  const errorBlock = (q, testid) => (
    <div className="m-5 rounded-3xl border border-black/5 bg-white p-10 text-center" data-testid={testid}>
      <p className="text-ink font-medium">{friendlyError(q.error)}</p>
      <button onClick={() => q.refetch()} className="mt-4 rounded-full bg-ink text-white px-5 py-2.5 text-sm font-medium">Try again</button>
    </div>
  );

  return (
    <div className="bg-paper min-h-screen" data-testid="property-detail-page">
      {/* ---- Mobile app-style page (<1024px) ---- */}
      {!isDesktop && (
        <div className="lg:hidden">
          {propQ.isLoading && <div className="p-5"><DetailSkeleton /></div>}
          {propQ.isError && errorBlock(propQ, "mdp-error")}
          {p && (
            <PropertyDetailMobile
              p={p} rooms={rooms} geo={geo} rev={rev} reviewsLoading={reviewsQ.isLoading}
              onShare={onShare} qs={qs} propertyId={propertyId} sp={sp}
            />
          )}
        </div>
      )}

      {/* ---- Desktop luxury layout (≥1024px) ---- */}
      {isDesktop && (
        <div className="hidden lg:block">
          {propQ.isLoading && <div className="max-w-7xl mx-auto px-6 py-10"><DetailSkeleton /></div>}
          {propQ.isError && errorBlock(propQ, "property-error")}
          {p && (
            <PropertyDetailDesktop
              p={p} rooms={rooms} geo={geo} rev={rev} reviewsLoading={reviewsQ.isLoading}
              onShare={onShare} propertyId={propertyId} sp={sp}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PropertyDetail;
